import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TYPES = ["fall", "bump", "bite", "scratch", "illness", "allergic_reaction", "behavior", "other"];
const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
const areaCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
const EMPTY = { type: "fall", location: "", description: "", injury_area: "", first_aid: "", witnesses: "", parent_notified_how: "" };

/** Teacher/admin incident (boo-boo) report for one child. */
export function IncidentDialog({ open, child, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(EMPTY); }, [open]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/daycare/incidents", { student_id: child.id, ...f });
      toast.success("Incident report submitted for admin review");
      onSaved?.(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="incident-dialog">
        <form onSubmit={save} className="space-y-3">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-600" /> Incident report</DialogTitle>
            <DialogDescription>{child?.full_name} · goes to the director for review, then to the parent to sign.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5">
            {TYPES.map((t) => <button key={t} type="button" onClick={() => setF((x) => ({ ...x, type: t }))} data-testid={`incident-type-${t}`}
              className={`rounded-full border px-3 py-1.5 text-xs capitalize ${f.type === t ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>{t.replace("_", " ")}</button>)}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={f.location} onChange={set("location")} placeholder="Where (playground, Room 1…)" className={inputCls} data-testid="incident-location" />
            <input value={f.injury_area} onChange={set("injury_area")} placeholder="Body area (left knee…)" className={inputCls} data-testid="incident-area" />
          </div>
          <textarea value={f.description} onChange={set("description")} rows={3} required minLength={3} placeholder="What happened? *" className={areaCls} data-testid="incident-description" />
          <textarea value={f.first_aid} onChange={set("first_aid")} rows={2} placeholder="First aid / action taken" className={areaCls} data-testid="incident-first-aid" />
          <div className="grid grid-cols-2 gap-2">
            <input value={f.witnesses} onChange={set("witnesses")} placeholder="Witnesses" className={inputCls} />
            <input value={f.parent_notified_how} onChange={set("parent_notified_how")} placeholder="Parent notified by (call, text…)" className={inputCls} />
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={busy || f.description.trim().length < 3} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50" data-testid="incident-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit report
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
